import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, CssBaseline, Dialog, DialogActions, DialogContent,
  DialogTitle, GlobalStyles, IconButton, Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginStep from './steps/LoginStep';
import DetailsStep from './steps/DetailsStep';
import ExamsStep from './steps/ExamsStep';
import HalachaExamsStep from './steps/HalachaExamsStep';
import SummaryStep from './steps/SummaryStep';
import RegisterStep from './steps/RegisterStep';
import {
  clearStudentSession,
  fetchStudentMeta,
  fetchStudentMe,
  fetchStudentGrades,
  getStudentToken,
} from './studentPortalApi';
import { getPortalTheme } from './portalTheme';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@500;700&family=Assistant:wght@400;600;700&display=swap';

const ENV_EXAM_MODE = process.env.REACT_APP_EXAM_MODE || 'predefined';
const ENV_SYSTEM_LABEL =
  process.env.REACT_APP_SYSTEM_LABEL ||
  (ENV_EXAM_MODE === 'open-gemara' ? 'מרא דתלמודא' : 'מרא דהלכתא');

function selectionFromRegistration(reg) {
  const map = {};
  for (const sel of reg?.gemaraSelection || []) {
    if (!sel.tractate) continue;
    map[sel.tractate] = (sel.ranges || []).map((r) => ({
      from: String(r.from ?? ''),
      to: String(r.to ?? ''),
    }));
  }
  return map;
}

function resolveLocation(member, meta) {
  const cities = meta?.examCities || [];
  if (meta?.examMode === 'predefined' || (!meta?.examMode && ENV_EXAM_MODE !== 'open-gemara')) {
    const fromRegs = (meta?.currentRegistrations || [])
      .map((r) => r.location)
      .find((loc) => loc && cities.includes(loc));
    if (fromRegs) return fromRegs;
  } else {
    const current = meta?.currentRegistration?.location;
    if (current && cities.includes(current)) return current;
  }
  if (member?.city && cities.includes(member.city)) return member.city;
  return cities[0] || member?.city || '';
}

const StudentPortal = () => {
  const [step, setStep] = useState(0);
  const [member, setMember] = useState(null);
  const [meta, setMeta] = useState(null);
  const [location, setLocation] = useState('');
  const [selectionMap, setSelectionMap] = useState({});
  const [gemaraSelection, setGemaraSelection] = useState([]);
  const [mergeMode, setMergeMode] = useState('new');
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const [booting, setBooting] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [bootError, setBootError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [gradePeriods, setGradePeriods] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);

  const examMode = meta?.examMode || ENV_EXAM_MODE;
  const isHalacha = examMode === 'predefined';
  const systemLabel = meta?.systemLabel || ENV_SYSTEM_LABEL;
  const theme = getPortalTheme(isHalacha);

  const steps = useMemo(
    () => [
      { key: 'login', label: 'זיהוי' },
      { key: 'details', label: 'פרטים' },
      { key: 'exams', label: isHalacha ? 'מבחנים' : 'חומר' },
      { key: 'summary', label: 'סיום' },
    ],
    [isHalacha]
  );

  const selectedExams = useMemo(() => {
    const open = meta?.openExams || [];
    const idSet = new Set(selectedExamIds);
    return open.filter((e) => idSet.has(String(e.id)));
  }, [meta?.openExams, selectedExamIds]);

  const logout = useCallback(() => {
    clearStudentSession();
    setMember(null);
    setMeta(null);
    setLocation('');
    setSelectionMap({});
    setGemaraSelection([]);
    setMergeMode('new');
    setSelectedExamIds([]);
    setGradePeriods([]);
    setGradesLoading(false);
    setStep(0);
    setBootError('');
    setShowRegister(false);
  }, []);

  const loadMetaAndMember = useCallback(async () => {
    const [meData, metaData, gradesResult] = await Promise.all([
      fetchStudentMe(),
      fetchStudentMeta(),
      fetchStudentGrades().catch(() => ({ periods: [] })),
    ]);
    setMember(meData.member);
    setMeta(metaData);
    setLocation(resolveLocation(meData.member, metaData));

    if (metaData.examMode === 'predefined') {
      const openIds = new Set((metaData.openExams || []).map((e) => String(e.id)));
      const ids = (metaData.currentRegistrations || [])
        .map((r) => r.examId)
        .filter(Boolean)
        .map(String)
        .filter((id) => openIds.has(id));
      setSelectedExamIds(ids);
      setGradePeriods(gradesResult?.periods || []);
    } else {
      setGradePeriods([]);
      const hasPrevious = metaData.previousRegistration?.gemaraSelection?.length > 0;
      if (!hasPrevious && metaData.currentRegistration?.gemaraSelection?.length) {
        setSelectionMap(selectionFromRegistration(metaData.currentRegistration));
      } else if (hasPrevious) {
        setSelectionMap({});
      }
    }
    setGradesLoading(false);
    return { meData, metaData };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!document.getElementById('mara-fonts')) {
        const link = document.createElement('link');
        link.id = 'mara-fonts';
        link.rel = 'stylesheet';
        link.href = FONT_HREF;
        document.head.appendChild(link);
      }

      const token = getStudentToken();
      if (!token) {
        if (!cancelled) setBooting(false);
        return;
      }
      try {
        await loadMetaAndMember();
        if (!cancelled) setStep(1);
      } catch {
        clearStudentSession();
        if (!cancelled) setBootError('פג תוקף ההתחברות. התחברו מחדש.');
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadMetaAndMember]);

  const handleLoginSuccess = async (loggedMember) => {
    setMember(loggedMember);
    setBootError('');
    try {
      const { metaData } = await loadMetaAndMember();
      setMember((prev) => ({ ...prev, ...loggedMember }));
      if (!metaData?.activePeriod) {
        setBootError('אין זמן מבחנים פעיל. ניתן לצפות בפרטים אך לא להירשם.');
      }
      setStep(1);
    } catch (err) {
      setBootError(err.response?.data?.error || 'שגיאה בטעינת נתונים');
      setStep(1);
    }
  };

  const stepIndex = Math.min(step, steps.length - 1);

  return (
    <>
      <CssBaseline />
      <GlobalStyles styles={{
        html: { background: theme.htmlBg, overflowX: 'hidden' },
        body: {
          background: `${theme.htmlBg} !important`,
          color: '#f5f0e6',
          overflowX: 'hidden',
          margin: 0,
          WebkitTextSizeAdjust: '100%',
        },
        '#root': { overflowX: 'hidden', maxWidth: '100vw' },
      }}
      />
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '100dvh', sm: '100vh' },
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          direction: 'rtl',
          fontFamily: '"Assistant", "Rubik", sans-serif',
          background: theme.pageBg,
          pb: { xs: 'max(24px, env(safe-area-inset-bottom))', sm: 6 },
          pt: 'env(safe-area-inset-top)',
          ...(theme.gridBg && {
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: theme.gridBg,
              backgroundSize: theme.gridSize || '160px 160px',
              backgroundPosition: 'center',
              backgroundRepeat: 'repeat',
              opacity: theme.gridOpacity ?? 0.12,
              pointerEvents: 'none',
              zIndex: 0,
              maskImage: 'radial-gradient(ellipse at 50% 30%, black 0%, transparent 78%)',
              WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 0%, transparent 78%)',
            },
          }),
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 2, sm: 3 },
            py: 1.5,
          }}
        >
          <IconButton
            onClick={() => setHelpOpen(true)}
            sx={{ color: 'rgba(245,240,230,0.7)' }}
            aria-label="עזרה"
          >
            <HelpOutlineIcon />
          </IconButton>
          {member && (
            <Button
              startIcon={<LogoutIcon />}
              onClick={logout}
              sx={{ color: 'rgba(245,240,230,0.75)', fontSize: '0.85rem' }}
            >
              יציאה
            </Button>
          )}
        </Box>

        <Box sx={{ textAlign: 'center', px: 2, mb: 3 }}>
          <Box
            component="img"
            src={theme.logo}
            alt={systemLabel}
            sx={{
              width: theme.logoWidth,
              maxWidth: 'min(92vw, 320px)',
              height: 'auto',
              mb: theme.showTitle ? 1.5 : 0.5,
              filter: 'drop-shadow(0 10px 28px rgba(0,0,0,0.45))',
            }}
          />
          {theme.showTitle && (
            <Typography
              sx={{
                fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.1rem' },
                color: theme.accent,
                letterSpacing: '0.02em',
                textShadow: `0 2px 12px ${theme.accent}55`,
              }}
            >
              {systemLabel}
            </Typography>
          )}
          <Typography sx={{ color: 'rgba(245,240,230,0.78)', mt: 0.5, fontWeight: 600 }}>
            רישום למבחנים
          </Typography>
        </Box>

        {!showRegister && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 },
            px: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          {steps.map((s, i) => {
            const active = i === stepIndex;
            const done = i < stepIndex;
            return (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: active || done ? '#0a1f14' : 'rgba(245,240,230,0.55)',
                    background: active || done
                      ? theme.stepActiveBg
                      : 'rgba(255,255,255,0.06)',
                    border: active || done ? 'none' : `1px solid ${theme.stepInactiveBorder}`,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography
                  sx={{
                    color: active ? theme.accent : 'rgba(245,240,230,0.55)',
                    fontWeight: active ? 700 : 500,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        )}

        <Dialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          dir="rtl"
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              m: { xs: 2, sm: 3 },
              width: { xs: 'calc(100% - 32px)', sm: 'auto' },
              borderRadius: 3,
              border: `1px solid ${theme.dialogBorder}`,
              background: theme.dialogBg,
              color: '#f5f0e6',
            },
          }}
        >
          <DialogTitle sx={{ color: theme.accent, fontWeight: 700, fontFamily: '"Frank Ruhl Libre", serif' }}>
            איך נרשמים?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'rgba(245,240,230,0.85)', fontSize: '0.95rem', lineHeight: 1.9 }}>
              1. הזדהות — טלפון וקוד אישי (או רישום לנבחן חדש).
              <br />
              {isHalacha ? (
                <>
                  2. עדכון הפרטים האישיים ובחירת מוקד בחינה.
                  <br />
                  3. בחירת מבחנים פתוחים (ניתן לבחור כמה).
                  <br />
                  4. סיכום ואישור — ההרשמה נשמרת במערכת.
                </>
              ) : (
                <>
                  2. אישור הפרטים האישיים ובחירת מוקד בחינה.
                  <br />
                  3. בחירת מסכתות וטווחי דפים.
                  <br />
                  4. סיכום ואישור — ההרשמה נשמרת במערכת.
                </>
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center' }}>
            <Button
              onClick={() => setHelpOpen(false)}
              sx={{
                px: 3,
                fontWeight: 700,
                borderRadius: 2,
                color: '#0a1f14',
                background: theme.stepActiveBg,
                '&:hover': { filter: 'brightness(1.06)' },
              }}
            >
              סגור
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            maxWidth: 720,
            width: { xs: 'calc(100% - 24px)', sm: '100%' },
            mx: 'auto',
            px: { xs: 1.5, sm: 3 },
            py: { xs: 2, sm: 3.5 },
            borderRadius: { xs: 2, sm: 3 },
            border: `1px solid ${theme.cardBorder}`,
            background: theme.cardBg,
            boxShadow: `0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 ${theme.cardBorder}`,
            boxSizing: 'border-box',
          }}
        >
          {booting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: theme.progressColor }} />
            </Box>
          ) : (
            <>
              {bootError && (
                <Typography sx={{ color: '#f5a9a9', textAlign: 'center', mb: 2 }}>{bootError}</Typography>
              )}

              {step === 0 && !showRegister && (
                <LoginStep
                  onSuccess={handleLoginSuccess}
                  onRegisterClick={() => setShowRegister(true)}
                  theme={theme}
                />
              )}

              {step === 0 && showRegister && (
                <RegisterStep onBack={() => setShowRegister(false)} theme={theme} />
              )}

              {step === 1 && member && (
                <DetailsStep
                  member={member}
                  examCities={meta?.examCities || []}
                  location={location}
                  setLocation={setLocation}
                  onBack={logout}
                  onNext={() => setStep(2)}
                  editable={isHalacha}
                  kolels={meta?.kolels || []}
                  onMemberUpdated={(updated) => setMember((prev) => ({ ...prev, ...updated }))}
                  systemLabel={systemLabel}
                  theme={theme}
                  gradePeriods={isHalacha ? gradePeriods : []}
                  gradesLoading={isHalacha ? gradesLoading : false}
                  showGrades={isHalacha}
                />
              )}

              {step === 2 && isHalacha && (
                <HalachaExamsStep
                  openExams={meta?.openExams || []}
                  activePeriod={meta?.activePeriod}
                  selectedExamIds={selectedExamIds}
                  setSelectedExamIds={setSelectedExamIds}
                  onBack={() => setStep(1)}
                  onNext={() => setStep(3)}
                  theme={theme}
                />
              )}

              {step === 2 && !isHalacha && (
                <ExamsStep
                  tractates={meta?.tractates || {}}
                  activePeriod={meta?.activePeriod}
                  previousRegistration={meta?.previousRegistration}
                  previousPeriod={meta?.previousPeriod}
                  currentRegistration={meta?.currentRegistration}
                  selectionMap={selectionMap}
                  setSelectionMap={setSelectionMap}
                  mergeMode={mergeMode}
                  setMergeMode={setMergeMode}
                  onBack={() => setStep(1)}
                  onNext={(built) => {
                    setGemaraSelection(built);
                    setStep(3);
                  }}
                />
              )}

              {step === 3 && (
                <SummaryStep
                  mode={examMode}
                  member={member}
                  location={location}
                  activePeriod={meta?.activePeriod}
                  gemaraSelection={gemaraSelection}
                  mergePrevious={mergeMode}
                  previousPeriod={meta?.previousPeriod}
                  previousRegistration={meta?.previousRegistration}
                  selectedExamIds={selectedExamIds}
                  selectedExams={selectedExams}
                  onBack={() => setStep(2)}
                  theme={theme}
                />
              )}
            </>
          )}
        </Box>
        </Box>
      </Box>
    </>
  );
};

export default StudentPortal;
